/**
 * The client side of the one auth interface.
 *
 * Firebase sits behind this. Nothing in either front end imports firebase
 * directly, so replacing the identity provider is a change to this file.
 *
 * Sign-in is email magic link and Google. No phone, no passwords.
 * docs/ENVIRONMENT.md §1.2.
 */

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";

export interface PublicConfig {
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId: string;
    messagingSenderId: string;
  };
  signInMethods: string[];
  district: string;
}

export interface SessionUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface AuthClient {
  /** Fires whenever the signed-in user changes, including on first load. */
  subscribe(listener: (user: SessionUser | null) => void): () => void;
  /** The bearer token for API calls, or null when signed out. */
  token(): Promise<string | null>;
  sendMagicLink(email: string): Promise<void>;
  /** True where the current URL is a magic link that can be completed. */
  isMagicLink(): boolean;
  completeMagicLink(emailFallback?: string): Promise<SessionUser>;
  signInWithGoogle(): Promise<SessionUser>;
  signOut(): Promise<void>;
}

const EMAIL_KEY = "ksidc.magiclink.email";

function toSessionUser(u: User): SessionUser {
  return { uid: u.uid, email: u.email, displayName: u.displayName };
}

export function createAuthClient(config: PublicConfig): AuthClient {
  const app: FirebaseApp = initializeApp(config.firebase);
  const auth: Auth = getAuth(app);

  return {
    subscribe(listener) {
      return onAuthStateChanged(auth, (u) => listener(u ? toSessionUser(u) : null));
    },

    async token() {
      const u = auth.currentUser;
      return u ? await u.getIdToken() : null;
    },

    async sendMagicLink(email: string) {
      await sendSignInLinkToEmail(auth, email, {
        url: window.location.origin + window.location.pathname,
        handleCodeInApp: true,
      });
      window.localStorage.setItem(EMAIL_KEY, email);
    },

    isMagicLink() {
      return isSignInWithEmailLink(auth, window.location.href);
    },

    async completeMagicLink(emailFallback?: string) {
      const stored = window.localStorage.getItem(EMAIL_KEY) ?? emailFallback;
      if (!stored) throw new Error("NEED_EMAIL");
      const result = await signInWithEmailLink(auth, stored, window.location.href);
      window.localStorage.removeItem(EMAIL_KEY);
      // Strip the one-time link parameters so a refresh does not replay them.
      window.history.replaceState({}, "", window.location.pathname);
      return toSessionUser(result.user);
    },

    async signInWithGoogle() {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      return toSessionUser(result.user);
    },

    async signOut() {
      await signOut(auth);
    },
  };
}
